import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { t } from 'i18next';
import { ColumnType } from 'antd/lib/table';
import { useWindowSize } from 'usehooks-ts';

import './index.less';

import { defaultApi } from '../../apis';
import {
  ApiKeyTenantScopeRole,
  ApiKeyProjectScopeRole,
  ApiKeyAgentScopeRole,
  ApiKeyCreationRequest,
  ApiKeyModel,
  ApiKeyScopeRole,
  ApiKeyUpdatingRequest,
  ApiKeyScopeRefType,
} from '../../../gamma-api';
import { alertsSelectorAdd } from '../../recoil';
import IbButton from '../../components/common/IbButton';
import IbTable, { MOBILE_ACTIONS_CELL_CLASS_NAME, MOBILE_MAIN_CELL_CLASS_NAME } from '../../components/common/IbTable';
import IbIcon from '../../components/common/IbIcon';
import { LARGE_PAGE_SIZE, LAYOUT_WIDTH_BREAKPOINTS } from '../../constants';
import IbConfirmModal from '../../components/common/IbConfirmModal';
import IbTypography from '../../components/common/IbTypography';
import { mask, MaskingMode } from '../../utils/stringUtil';
import { tryGetValidationErrors } from '../../utils/errorUtils';
import { ValidationErrors } from '../../types';

import {
  MAIN_CLASS_NAME,
  ACTIONS_DATA_INDEX,
  ACCESS_CELL_CLASS_NAME,
  COPY_ID_BUTTON_CLASS_NAME,
  X_TOKEN_LENGTH,
  X_TOKEN_VISIBLE_LENGTH,
  TABLE_CLASS_NAME,
  ACTIONS_CLASS_NAME,
  HEADER_CLASS_NAME,
  REF_ID_CLASS_NAME,
} from './const';
import ApiKeyModal from './ApiKeyModal';

export enum ApiKeysMode {
  Default = 'Default',
  Creation = 'Creation',
}

interface ApiKeyScopeRef {
  type: ApiKeyScopeRefType;
  id?: string;
  externalId?: string;
}

interface IApiKeysProps {
  scopeRef: ApiKeyScopeRef;
  showCreationButton?: boolean;
  mode?: ApiKeysMode;
  onModeToggle?: (mode: ApiKeysMode) => void;
}

const ApiKeys: React.FC<IApiKeysProps> = ({ scopeRef, showCreationButton, mode, onModeToggle }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const windowSize = useWindowSize();
  const isDesktop = windowSize.width > LAYOUT_WIDTH_BREAKPOINTS.small;

  const [loading, setLoading] = useState(false);

  const [apiKeys, setApiKeys] = useState<ApiKeyModel[]>([]);

  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [editApiKey, setEditApiKey] = useState<ApiKeyModel>();
  const [deleteApiKey, setDeleteApiKey] = useState<ApiKeyModel>();

  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const response = await defaultApi.searchApiKeysSecurityApiKeysGet(
        [scopeRef.type],
        scopeRef.id ? [scopeRef.id] : undefined,
        0,
        LARGE_PAGE_SIZE,
        true
      );
      setApiKeys(response.data.items || []);
    } catch (err) {
      addAlert({ content: t('API Keys: Failed to load keys'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys().finally();
  }, []);

  const toggleModeValue = (value: ApiKeysMode) => {
    if (mode && mode != value) {
      onModeToggle?.(value);
    }
  };

  const startApiKeyCreation = () => {
    setEditApiKey(undefined);
    setApiKeyModalVisible(true);
    toggleModeValue(ApiKeysMode.Creation);
  };

  const finishApiKeyEditing = () => {
    setApiKeyModalVisible(false);
    setEditApiKey(undefined);
    toggleModeValue(ApiKeysMode.Default);
  };

  const onModeChange = () => {
    if (mode == ApiKeysMode.Creation) {
      startApiKeyCreation();
    } else if (mode == ApiKeysMode.Default) {
      finishApiKeyEditing();
    }
  };

  useEffect(onModeChange, [mode]);

  const onCreateApiKeyClick = () => {
    startApiKeyCreation();
  };

  const onApiKeyModalCancel = () => {
    finishApiKeyEditing();
  };

  const onEditApiKey = (apiKey: ApiKeyModel) => {
    setEditApiKey(apiKey);
    setApiKeyModalVisible(true);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteApiKey) return;

    setDeleteConfirmLoading(true);
    try {
      await defaultApi.deleteOneApiKeySecurityApiKeysApiKeyIdDelete(deleteApiKey.api_key_id);
      await loadApiKeys();
    } catch (e) {
      addAlert({ content: t('API Keys: ApiKey deletion error'), type: 'error', icon: <IbIcon iconName="attention" /> });
    }
    setDeleteConfirmLoading(false);
    setDeleteApiKey(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteApiKey(undefined);
  };

  const onDeleteApiKey = (apiKey: ApiKeyModel) => {
    setDeleteApiKey(apiKey);
  };

  const onApiKeyAdd = async (
    apiKey: ApiKeyCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    try {
      await defaultApi.createApiKeySecurityApiKeysPost(apiKey);

      finishApiKeyEditing();

      await loadApiKeys();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: t('API Keys: ApiKey creating error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onApiKeyEdit = async (
    apiKey: ApiKeyUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!editApiKey) return;

    try {
      await defaultApi.updateApiKeySecurityApiKeysApiKeyIdPut(editApiKey.api_key_id, apiKey);

      finishApiKeyEditing();

      await loadApiKeys();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: t('API Keys: ApiKey updating error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onCopyExternalIdButtonClick = () => {
    if (!scopeRef.externalId) return;
    void navigator.clipboard.writeText(scopeRef.externalId);
  };

  const getApiKeyRoleText = (role: ApiKeyScopeRole): string => {
    switch (role) {
      case ApiKeyTenantScopeRole.Integration:
        return t('API Keys: Tenant: Integration');
      case ApiKeyProjectScopeRole.Chat:
        return t('API Keys: Project: Chat');
      case ApiKeyProjectScopeRole.DataManagement:
        return t('API Keys: Project: Data management');
      case ApiKeyAgentScopeRole.Chat:
        return t('API Keys: Agent: Chat');
      default:
        return `${role}`;
    }
  };

  const columns: ColumnType<ApiKeyModel>[] = [
    {
      title: t('API Keys: Name'),
      className: MOBILE_MAIN_CELL_CLASS_NAME,
      render: (_, record) => {
        return record.name;
      },
      width: isDesktop ? '25%' : undefined,
    },
    {
      title: t('API Keys: Key'),
      render: (_, record) => {
        const onCopyIdButtonClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
          e.stopPropagation();
          navigator.clipboard.writeText(record.x_token);
        };

        return (
          <div className={ACCESS_CELL_CLASS_NAME}>
            <IbTypography.Paragraph>
              {mask(record.x_token, MaskingMode.End, X_TOKEN_LENGTH, X_TOKEN_VISIBLE_LENGTH)}
            </IbTypography.Paragraph>
            <IbButton
              className={COPY_ID_BUTTON_CLASS_NAME}
              icon={<IbIcon iconName="copy-one" />}
              type="link"
              onClick={onCopyIdButtonClick}
            />
          </div>
        );
      },
      width: isDesktop ? '40%' : undefined,
    },
    {
      title: t('API Keys: Access'),
      render: (_, record) => {
        return record.roles
          .sort()
          .map((role) => getApiKeyRoleText(role))
          .join(', ');
      },
    },
    {
      className: [ACTIONS_CLASS_NAME, MOBILE_ACTIONS_CELL_CLASS_NAME].join(' '),
      title: '',
      render: (_, record) => {
        const onDeleteButtonClick = () => {
          onDeleteApiKey(record);
        };

        return <IbButton icon={<IbIcon iconName="delete" onClick={onDeleteButtonClick} />} type="icon" />;
      },
      dataIndex: ACTIONS_DATA_INDEX,
    },
  ];

  const onTableRowClick = (apiKey: ApiKeyModel, column: ColumnType<unknown>) => {
    if (column.dataIndex === ACTIONS_DATA_INDEX) {
      return;
    }

    onEditApiKey(apiKey);
  };

  const getApiKeyRowKey = (apiKey: ApiKeyModel) => apiKey.api_key_id;

  const renderHeader = () => {
    if (!showCreationButton && !scopeRef.externalId) {
      return null;
    }

    return (
      <div className={HEADER_CLASS_NAME}>
        {showCreationButton && (
          <IbButton icon={<IbIcon iconName="add-one" />} type="primary" onClick={onCreateApiKeyClick}>
            {t('API Keys: Add key')}
          </IbButton>
        )}
        {scopeRef.externalId && (
          <div className={REF_ID_CLASS_NAME}>
            <IbTypography.Paragraph disabled>{t(`API Keys: ${scopeRef.type}: Id: Label`)}:</IbTypography.Paragraph>
            <IbTypography.Paragraph>{scopeRef.externalId}</IbTypography.Paragraph>
            <IbButton icon={<IbIcon iconName="copy-one" />} type="icon" onClick={onCopyExternalIdButtonClick} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderHeader()}
      <IbTable
        className={TABLE_CLASS_NAME}
        columns={columns}
        dataSource={apiKeys}
        loading={loading}
        rowKey={getApiKeyRowKey}
        onRowClick={onTableRowClick}
      />
      <ApiKeyModal
        editApiKey={editApiKey}
        scopeRef={scopeRef}
        visible={apiKeyModalVisible}
        onAdd={onApiKeyAdd}
        onCancel={onApiKeyModalCancel}
        onEdit={onApiKeyEdit}
      />
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onDeleteConfirmButtonClick}>
              {t('API Keys: Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onDeleteCancelButtonClick}>
              {t('API Keys: Cancel')}
            </IbButton>
          </>
        }
        loading={deleteConfirmLoading}
        title={t('API Keys: Delete key?')}
        visible={!!deleteApiKey}
        onCancel={onDeleteCancelButtonClick}
      >
        <IbTypography.Paragraph type="secondary">
          {t('API Keys: Are you sure you want to remove the key?')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    </div>
  );
};

export default ApiKeys;
